/**
 * Store Markers Component
 *
 * Renders store markers on the map
 * following Single Responsibility Principle
 */

import React, {useMemo} from 'react';
import {Marker} from 'react-map-gl';
import {Store as StoreIcon} from 'lucide-react';
import {Store} from '../../../features/stores/models/store.model';

export interface StoreMarkersProps {
  stores: Store[];
  selectedStore?: Store;
  onStoreSelect: (store: Store) => void;
  filterValidStores: (stores: Store[]) => Store[];
  className?: string;
}

const getMarkerColorClasses = (isSelected: boolean): string => {
  const baseClasses = 'relative w-8 h-8 rounded-full border shadow-lg flex items-center justify-center transform transition-all';

  return isSelected
    ? `${baseClasses} bg-brand-600 border-white scale-110 ring-2 ring-brand-400 shadow-xl`
    : `${baseClasses} bg-brand-500 border-white hover:scale-105 shadow-md`;
};

export const StoreMarkers: React.FC<StoreMarkersProps> = ({
  stores,
  selectedStore,
  onStoreSelect,
  filterValidStores,
  className = ''
}) => {
  const storeMarkers = useMemo(() => {
    const validStores = filterValidStores(stores);

    return validStores.map((store) => {
      const isSelected = selectedStore?.id === store.id;
      const handleClick = () => onStoreSelect(store);

      return (
        <Marker
          key={`store-${store.id}`}
          longitude={store.longitude}
          latitude={store.latitude}
          anchor="bottom"
          onClick={handleClick}
        >
          <div
            className={`cursor-pointer group relative ${className}`}
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            {/* Main Marker */}
            <div className={getMarkerColorClasses(isSelected)}>
              <StoreIcon className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            {/* Tooltip */}
            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2.5 py-1.5 bg-gray-900 text-white text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap shadow-lg z-10">
              {store.name}
            </div>
          </div>
        </Marker>
      );
    });
  }, [stores, selectedStore?.id, onStoreSelect, filterValidStores, className]);

  return <>{storeMarkers}</>;
};

export default StoreMarkers;
